import React, { PureComponent } from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import classNames from 'classnames';
import { NotificationManager } from 'choerodon-ui/shared';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';
import scrollIntoView from 'scroll-into-view-if-needed';
import Animate from '../animate';
import Notice from './Notice';
import { getNoticeLocale } from './locale';
import EventManager from '../_util/EventManager';
import { getStyle } from '../rc-components/util/Dom/css';
export function newNotificationInstance(properties, callback) {
    const { getContainer, ...props } = properties || {};
    const div = document.createElement('div');
    if (getContainer) {
        const root = getContainer();
        root.appendChild(div);
    }
    else {
        document.body.appendChild(div);
    }
    let called = false;
    function ref(notification) {
        if (called) {
            return;
        }
        called = true;
        callback({
            notice(noticeProps) {
                notification.add(noticeProps);
            },
            removeNotice(key) {
                notification.remove(key);
            },
            destroy() {
                unmountComponentAtNode(div);
                const { parentNode } = div;
                if (parentNode) {
                    parentNode.removeChild(div);
                }
            },
        });
    }
    render(React.createElement(Notification, Object.assign({}, props, { ref: ref })), div);
}
export default class Notification extends PureComponent {
    constructor() {
        super(...arguments);
        this.scrollRef = null;
        this.state = {
            notices: [],
            scrollHeight: 'auto',
            totalHeight: 0,
            offset: 0,
        };
        this.onAnimateEnd = () => {
            const { notices } = this.state;
            const { foldCount } = this.props;
            if (foldCount) {
                const { scrollRef } = this;
                if (scrollRef) {
                    const childSpan = scrollRef.firstChild;
                    if (!childSpan)
                        return;
                    const childNodes = childSpan.childNodes;
                    const lastNode = childNodes[childNodes.length - 1];
                    if (childNodes.length > foldCount && notices.length > foldCount) {
                        let totalHeight = 0;
                        for (let i = 0; i < childNodes.length; i += 1) {
                            const element = childNodes[i];
                            totalHeight += element.offsetHeight + getStyle(element, 'margin-bottom');
                        }
                        const scrollHeight = (totalHeight / childNodes.length) * (foldCount + 0.5);
                        this.setState({
                            scrollHeight,
                            totalHeight,
                        }, () => {
                            if (!this.isRemove) {
                                scrollIntoView(lastNode, {
                                    block: 'center',
                                    behavior: 'smooth',
                                    scrollMode: 'if-needed',
                                    boundary: scrollRef,
                                });
                            }
                        });
                    }
                    else {
                        this.setState({
                            scrollHeight: 'auto',
                        });
                    }
                }
            }
        };
        this.clearNotices = () => {
            this.setState({
                notices: [],
            });
        };
        this.handleNoticeClose = (eventKey) => {
            const { notices } = this.state;
            const notice = notices.find(({ key }) => key === eventKey);
            this.remove(eventKey);
            if (notice) {
                const { onClose = noop } = notice;
                onClose(eventKey);
            }
        };
        this.saveScrollRef = (dom) => {
            this.scrollRef = dom;
            if (dom) {
                const debouncedResize = debounce((e) => {
                    this.setState({
                        offset: e.target.scrollTop,
                    });
                }, 200);
                this.scrollEvent = new EventManager(dom).addEventListener('scroll', debouncedResize);
            }
            else {
                this.dispose();
            }
        };
    }
    dispose() {
        const { scrollEvent } = this;
        if (scrollEvent) {
            scrollEvent.clear();
        }
    }
    componentWillUnmount() {
        this.dispose();
    }
    getTransitionName() {
        const { transitionName, animation, prefixCls } = this.props;
        if (!transitionName && animation) {
            return `${prefixCls}-${animation}`;
        }
        return transitionName;
    }
    add(notice) {
        if (!notice.key) {
            notice.key = NotificationManager.getUuid();
        }
        const { key } = notice;
        const { maxCount } = this.props;
        this.setState(previousState => {
            const notices = previousState.notices;
            if (!notices.filter(v => v.key === key).length) {
                if (maxCount && notices && notices.length > 0 && notices.length >= maxCount) {
                    notices.shift();
                }
                this.isRemove = false;
                return {
                    ...previousState,
                    notices: notices.concat(notice),
                };
            }
        });
    }
    remove(key) {
        this.setState(previousState => {
            this.isRemove = true;
            const notices = previousState.notices.filter(notice => notice.key !== key);
            return {
                notices,
            };
        });
    }
    render() {
        const { notices, scrollHeight, offset, totalHeight } = this.state;
        const { contentClassName, prefixCls, closeIcon, className, style, foldCount } = this.props;
        const noticeNodes = notices.map((notice) => (React.createElement(Notice, Object.assign({ prefixCls: prefixCls, contentClassName: contentClassName }, notice, { onClose: this.handleNoticeClose, closeIcon: closeIcon, key: notice.key, eventKey: notice.key, foldable: !!foldCount, offset: offset, scrollHeight: scrollHeight, totalHeight: totalHeight }))));
        const cls = classNames(`${prefixCls}`, className, [{
                [`${prefixCls}-before-shadow`]: !!foldCount && notices.length > foldCount && offset > 0,
                [`${prefixCls}-after-shadow`]: foldCount && notices.length > foldCount &&
                    totalHeight - (typeof scrollHeight === 'number' ? scrollHeight : 0) - offset > 15,
            }]);
        const scrollCls = classNames({
            [`${prefixCls}-scroll`]: !!foldCount && scrollHeight !== 'auto',
        });
        const runtimeLocale = getNoticeLocale();
        return (React.createElement("div", { className: cls, style: style },
            React.createElement("div", { className: scrollCls, style: foldCount ? {
                    height: scrollHeight,
                } : undefined, ref: foldCount ? this.saveScrollRef : undefined },
                React.createElement(Animate, { onEnd: this.onAnimateEnd, transitionName: this.getTransitionName() }, noticeNodes)),
            foldCount && notices.length > foldCount && (React.createElement("div", { className: `${prefixCls}-alert` },
                React.createElement("div", { className: `${prefixCls}-alert-message` }, `${runtimeLocale.total} ${notices.length} ${runtimeLocale.message}`),
                React.createElement("div", { className: `${prefixCls}-alert-close`, onClick: this.clearNotices }, `${runtimeLocale.closeAll}`)))));
    }
}
Notification.defaultProps = {
    prefixCls: 'c7n-notification',
    animation: 'fade',
    style: {
        top: 65,
        left: '50%',
    },
};
Notification.newInstance = newNotificationInstance;
